import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Table,
  InputNumber,
} from 'antd';
import { ArrowLeftOutlined, CheckOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { invoiceService, invoicePaymentService, CreateInvoiceData, Invoice, InvoicePayment, CreateInvoicePaymentData } from '../services/invoices';
import { quotationService } from '../services/quotations';
import { customerService } from '../services/customers';
import { staffService, Staff } from '../services/staffs';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

export const InvoiceFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'invoices.update');
  const canDelete = hasPermission(user, 'invoices.delete');
  const [submitting, setSubmitting] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [quotations, setQuotations] = useState<any[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [payments, setPayments] = useState<InvoicePayment[]>([]);
  const [paymentsToDelete, setPaymentsToDelete] = useState<number[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [selectedQuotation, setSelectedQuotation] = useState<any>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [invoiceName, setInvoiceName] = useState<string>('');
  const [isEditing, setIsEditing] = useState(!isEdit); // For new invoices, default to editing mode
  const [invoiceItems, setInvoiceItems] = useState<any[]>([]);
  const [taskTypes, setTaskTypes] = useState<any[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInitialData();
    if (isEdit && id) {
      loadInvoice(parseInt(id, 10));
      loadPayments(parseInt(id, 10));
    }
  }, [isEdit, id]);

  const fetchInitialData = async () => {
    try {
      const [customersResponse, quotationsResponse, staffResponse, taskTypesResponse, taskTemplatesResponse] = await Promise.all([
        customerService.getAll(),
        quotationService.getAll(),
        staffService.getAll().catch(() => []),
        api.get('/task-types').catch(() => ({ data: { data: [] } })),
        api.get('/task-templates').catch(() => ({ data: { data: [] } })),
      ]);
      
      setCustomers(customersResponse.data || []);
      setQuotations(quotationsResponse.data || []);
      setStaff(Array.isArray(staffResponse) ? staffResponse : []);
      setTaskTypes(taskTypesResponse.data?.data || taskTypesResponse.data || []);
      setTaskTemplates(taskTemplatesResponse.data?.data || taskTemplatesResponse.data || []);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      message.error('Failed to load initial data');
    }
  };

  const loadInvoice = async (invoiceId: number) => {
    setLoadingInvoice(true);
    try {
      const invoice = await invoiceService.getById(invoiceId);
      form.setFieldsValue({
        ...invoice,
        date: invoice.date ? dayjs(invoice.date) : undefined,
        status: invoice.status || 'draft',
      });
      
      // Load invoice items
      setInvoiceItems((invoice as any).items || []);
      
      // Load quotation if exists
      if (invoice.quotation_id) {
        try {
          const quotation = await quotationService.getById(invoice.quotation_id);
          setSelectedQuotation(quotation);
          setTasks(quotation.tasks || []);
        } catch {
          // If quotation load fails, use invoice.quotation if available
          if (invoice.quotation) {
            setSelectedQuotation(invoice.quotation);
            setTasks(invoice.quotation.tasks || []);
          }
        }
      }
      
      // Load full customer data with contacts
      if (invoice.customer_id) {
        try {
          const customer = await customerService.getById(invoice.customer_id);
          setSelectedCustomer(customer);
          setCustomers(prev => {
            const existing = prev.find(c => c.id === invoice.customer_id);
            if (existing) {
              return prev.map(c => c.id === invoice.customer_id ? customer : c);
            }
            return [...prev, customer];
          });
        } catch {
          // If customer load fails, use invoice.customer if available
          if (invoice.customer) {
            setSelectedCustomer(invoice.customer);
          }
        }
      }
    } catch {
      message.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoadingInvoice(false);
    }
  };

  const loadPayments = async (invoiceId: number) => {
    try {
      const response = await invoicePaymentService.getAll(invoiceId);
      const paymentsData = (response.data || []).map((payment: InvoicePayment) => ({
        ...payment,
        type: payment.type || 'official_receipt', // Set default type if missing
      }));
      setPayments(paymentsData);
      setPaymentsToDelete([]); // Reset deletions when loading
    } catch {
      message.error('Failed to load payments');
    }
  };

  const handleQuotationChange = async (quotationId: number) => {
    if (quotationId) {
      try {
        const quotation = await quotationService.getById(quotationId);
        setSelectedQuotation(quotation);
        
        // Load full customer data with contacts
        if (quotation.customer_id) {
          try {
            const customer = await customerService.getById(quotation.customer_id);
            setSelectedCustomer(customer);
        setCustomers(prev => {
              const existing = prev.find(c => c.id === quotation.customer_id);
          if (existing) {
                return prev.map(c => c.id === quotation.customer_id ? customer : c);
          }
          return [...prev, customer];
            });
          } catch {
            // If customer load fails, use quotation.customer if available
            if (quotation.customer) {
              setSelectedCustomer(quotation.customer);
            }
          }
        }
        
        // Load tasks from quotation
        setTasks(quotation.tasks || []);
        
        // Auto-fill form fields from quotation
        form.setFieldsValue({
          quotation_id: quotationId,
          customer_id: quotation.customer_id || undefined,
          agent_id: quotation.agent_id || undefined,
          team_id: quotation.team_id || undefined,
          staff_id: quotation.staff_id || undefined,
        });
      } catch {
        message.error('Failed to load quotation details');
      }
    } else {
      setSelectedQuotation(null);
      setSelectedCustomer(null);
      setTasks([]);
      form.setFieldsValue({
        customer_id: undefined,
        agent_id: undefined,
        team_id: undefined,
        staff_id: undefined,
      });
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await invoiceService.delete(parseInt(id, 10));
      message.success('Invoice deleted');
      navigate('/invoices');
    } catch {
      message.error('Failed to delete invoice');
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      // Handle date - it might be a dayjs object or a string
      let dateValue = values.date;
      if (dayjs.isDayjs(dateValue)) {
        dateValue = dateValue.format('YYYY-MM-DD');
      } else if (typeof dateValue === 'string') {
        dateValue = dateValue;
      } else {
        dateValue = dayjs().format('YYYY-MM-DD');
      }

      const payload: CreateInvoiceData = {
        ...values,
        date: dateValue,
        // Remove invoice_number from payload - backend will generate it
        invoice_number: undefined,
      };

      let invoiceId: number;
      if (isEdit && id) {
        await invoiceService.update(parseInt(id, 10), payload);
        invoiceId = parseInt(id, 10);
        message.success('Invoice updated successfully');
      } else {
        // Create invoice functionality is disabled - invoices are created automatically when quotation is approved
        // const result = await invoiceService.create(payload);
        // invoiceId = result.id;
        // // Update form with generated invoice number
        // if (result.invoice_number) {
        //   form.setFieldsValue({ invoice_number: result.invoice_number });
        // }
        // message.success('Invoice created successfully');
        message.error('Creating new invoices is disabled. Invoices are created automatically when quotations are approved.');
        setSubmitting(false);
        return;
      }

      // Save payments after invoice is saved
      if (invoiceId) {
        // Delete payments marked for deletion
        for (const paymentId of paymentsToDelete) {
          try {
            await invoicePaymentService.delete(paymentId);
          } catch (error) {
            console.error(`Failed to delete payment ${paymentId}:`, error);
          }
        }

        // Create new payments and update existing ones
        for (const payment of payments) {
          const paymentPayload: CreateInvoicePaymentData = {
            invoice_id: invoiceId,
            type: payment.type || 'official_receipt',
            number: payment.number || undefined, // If empty, backend will auto-generate
            payment_date: payment.payment_date,
            payment_amount: typeof payment.payment_amount === 'string' ? parseFloat(payment.payment_amount) : payment.payment_amount,
            enabled: payment.enabled ?? true,
          };

          if (typeof payment.id === 'string' && payment.id.startsWith('temp-')) {
            // New payment - create it
            try {
              await invoicePaymentService.create(paymentPayload);
            } catch (error) {
              console.error('Failed to create payment:', error);
            }
          } else if (typeof payment.id === 'number') {
            // Existing payment - update it
            try {
              await invoicePaymentService.update(payment.id, paymentPayload);
            } catch (error) {
              console.error(`Failed to update payment ${payment.id}:`, error);
            }
          }
        }
      }
      
      navigate('/invoices');
    } catch (error: any) {
      console.error('Failed to submit invoice:', error);
      const errorMessage = error?.response?.data?.message || error?.message || (isEdit ? 'Failed to update invoice' : 'Failed to create invoice');
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = (paymentId: number | string) => {
    // If it's an existing payment (has numeric ID), mark it for deletion
    if (typeof paymentId === 'number') {
      setPaymentsToDelete(prev => [...prev, paymentId]);
    }
    // Remove from local state
    setPayments(prev => prev.filter(p => p.id !== paymentId));
  };

  const loadNextNumber = async (date?: string) => {
    try {
      const nextNumber = await invoiceService.getNextNumber(date);
      if (nextNumber) {
        form.setFieldsValue({ invoice_number: nextNumber });
      }
    } catch (error) {
      console.error('Failed to load next invoice number:', error);
      // If API fails, use a fallback preview
      const prefix = 'INV';
      const year = new Date().getFullYear().toString().slice(-2);
      const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
      form.setFieldsValue({ invoice_number: `${prefix}${year}${month}-001` });
    }
  };

  useEffect(() => {
    if (!isEdit) {
      const initialDate = dayjs();
      form.setFieldsValue({
        date: initialDate,
        enabled: true,
      });
      // Load next number from backend
      loadNextNumber(initialDate.format('YYYY-MM-DD'));
    }
  }, [isEdit, form]);

  // Update number when date changes
  const handleDateChange = (date: any) => {
    if (!isEdit && date) {
      loadNextNumber(date.format('YYYY-MM-DD'));
    }
  };

  const getInvoiceDisplayName = () => {
    if (isEdit && id) {
      const customer = selectedCustomer || customers.find(c => c.id === form.getFieldValue('customer_id'));
      // Try to get task type from invoice items first, then from tasks
      const taskType = invoiceItems.length > 0 && invoiceItems[0]?.task_type 
        ? invoiceItems[0].task_type 
        : (invoiceItems.length > 0 && invoiceItems[0]?.taskType 
          ? invoiceItems[0].taskType 
          : (tasks.length > 0 ? tasks[0]?.task_type : null));
      const customerType = customer?.type === 'business' ? 'Business' : 'Personal';
      return `${form.getFieldValue('invoice_number') || ''} - ${customerType} - ${taskType?.name || 'Task'}`;
    }
    return 'New Invoice';
  };

  useEffect(() => {
    if (isEdit && id && (customers.length > 0 || selectedCustomer) && (invoiceItems.length > 0 || tasks.length > 0)) {
      const displayName = getInvoiceDisplayName();
      setInvoiceName(displayName);
    }
  }, [isEdit, id, customers, selectedCustomer, invoiceItems, tasks, form.getFieldValue('invoice_number'), form.getFieldValue('customer_id')]);

  const paymentColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '20%',
      render: (type: string | undefined, record: InvoicePayment, index: number) => {
        if (isEditing) {
          return (
            <Select
              value={(type || 'official_receipt') as 'official_receipt' | 'credit_note'}
              onChange={(value: 'official_receipt' | 'credit_note') => {
                const updated = [...payments];
                updated[index] = { 
                  ...updated[index], 
                  type: value,
                  number: undefined, // Clear number when type changes, will be regenerated
                };
                setPayments(updated);
              }}
              style={{ width: '100%' }}
            >
              <Select.Option value="official_receipt">Official Receipt</Select.Option>
              <Select.Option value="credit_note">Credit Note</Select.Option>
            </Select>
          );
        }
        return (type || record.type) === 'credit_note' ? 'Credit Note' : 'Official Receipt';
      },
    },
    {
      title: 'No',
      dataIndex: 'number',
      key: 'number',
      width: '20%',
      render: (number: string, record: InvoicePayment, index: number) => {
        if (isEditing) {
          return (
            <Input
              value={number || ''}
              placeholder="Auto-generated"
              onChange={(e) => {
                const updated = [...payments];
                updated[index] = { 
                  ...updated[index], 
                  number: e.target.value || undefined,
                };
                setPayments(updated);
              }}
              style={{ width: '100%' }}
            />
          );
        }
        return number || '-';
      },
    },
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      width: '20%',
      render: (date: string, record: InvoicePayment, index: number) => {
        if (isEditing) {
          return (
            <DatePicker
              value={date ? dayjs(date) : undefined}
              onChange={(dateValue) => {
                const updated = [...payments];
                updated[index] = { 
                  ...updated[index], 
                  payment_date: dateValue ? dateValue.format('YYYY-MM-DD') : '',
                  number: undefined, // Clear number when date changes, will be regenerated
                };
                setPayments(updated);
              }}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          );
        }
        if (!date) return '-';
        try {
          return dayjs(date).format('DD/MM/YYYY');
        } catch {
          return date;
        }
      },
    },
    {
      title: 'Amount',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      width: '20%',
      render: (amount: number | string, record: InvoicePayment, index: number) => {
        if (isEditing) {
          return (
            <InputNumber
              value={typeof amount === 'string' ? parseFloat(amount) : (amount || 0)}
              onChange={(value: number | null) => {
                const updated = [...payments];
                updated[index] = { 
                  ...updated[index], 
                  payment_amount: value || 0,
                };
                setPayments(updated);
              }}
              style={{ width: '100%' }}
              min={0}
              precision={2}
              prefix="RM "
            />
          );
        }
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return `RM ${isNaN(numAmount) ? '0.00' : numAmount.toFixed(2)}`;
      },
    },
    ...(isEditing ? [{
      title: 'Action',
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: InvoicePayment, index: number) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeletePayment(record.id)}
        />
      ),
    }] : []),
  ];

  return (
    <>
      {isEdit && invoiceName && (
        <div style={{
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          {/* First row: Title and buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
            marginBottom: 12,
            paddingBottom: 12,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {invoiceName}
          </Title>
          <Space>
            {!isEditing && canUpdate && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
          </div>
        </div>
      )}
      
      {!isEdit && (
        <Title level={3} style={{ marginBottom: 24 }}>
          New Invoice
        </Title>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loadingInvoice || (isEdit && !isEditing)}
        onValuesChange={(changedValues) => {
          if (changedValues.customer_id !== undefined || changedValues.invoice_number !== undefined) {
            const displayName = getInvoiceDisplayName();
            setInvoiceName(displayName);
          }
        }}
        initialValues={{
          status: 'draft',
        }}
      >
        {/* Hidden field to register status in form */}
        <Form.Item name="status" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Row gutter={24}>
          {/* Left Column - Customer Information */}
            <Col span={12}>
            <Card title="Customer Information" style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {(() => {
                    const isBusinessCustomer = Boolean(
                      selectedQuotation?.business_id || (selectedQuotation as any)?.business
                    );
                    const label = isBusinessCustomer ? 'Business' : 'Customer';
                    const displayName = isBusinessCustomer
                      ? (selectedQuotation as any)?.business?.name || selectedQuotation?.customer?.name || selectedCustomer?.name
                      : selectedQuotation?.customer?.name || selectedCustomer?.name;
                    return (
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>{label}</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>{displayName || '-'}</div>
                        </td>
                      </tr>
                    );
                  })()}
                  {(() => {
                    const isBusinessCustomer = Boolean(
                      selectedQuotation?.business_id || (selectedQuotation as any)?.business
                    );
                    const customerTypeLabel = isBusinessCustomer ? 'Business' : 'Customer';
                    return (
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Type</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>{customerTypeLabel}</div>
                        </td>
                      </tr>
                    );
                  })()}
                  {/* Quotation - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Quotation</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <div>{selectedQuotation?.quotation_number || '-'}</div>
                    </td>
                  </tr>
                  <Form.Item name="customer_id" noStyle hidden>
                    <Input type="hidden" />
                  </Form.Item>
                  {/* Address - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Address</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.quotation_id !== currentValues.quotation_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              let addressText = '';
                              
                              if (selectedCustomer?.contacts && selectedCustomer.contacts.length > 0) {
                                const contact = selectedCustomer.contacts[0];
                                const addressParts: string[] = [];
                                if (contact.address1) addressParts.push(contact.address1);
                                if (contact.address2) addressParts.push(contact.address2);
                                addressText = addressParts.join('\n');
                              }
                              
                          return <div style={{ whiteSpace: 'pre-line' }}>{addressText || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Email - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Email</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.quotation_id !== currentValues.quotation_id
                          } noStyle>
                            {() => {
                              const isBusinessCustomer = Boolean(
                                selectedQuotation?.business_id || (selectedQuotation as any)?.business
                              );
                              const email =
                                (isBusinessCustomer
                                  ? (selectedQuotation as any)?.business?.contacts?.[0]?.email
                                  : selectedQuotation?.customer?.email) ||
                                selectedCustomer?.email ||
                                selectedCustomer?.contacts?.[0]?.email ||
                                '';
                          return <div>{email || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Attention - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Attention</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.quotation_id !== currentValues.quotation_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const attention = selectedQuotation?.customer?.name || selectedCustomer?.name || '';
                          return <div>{attention || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Contact - 单独一行 */}
                  <tr>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Contact</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.quotation_id !== currentValues.quotation_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              let contact = '';
                              
                              if (selectedCustomer?.contacts && selectedCustomer.contacts.length > 0) {
                                const contactData = selectedCustomer.contacts[0];
                                contact = contactData.contact_number || contactData.email || '';
                              }
                              
                          return <div>{contact || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
            </Col>

          {/* Right Column - Invoice Details */}
            <Col span={12}>
            <Card title="Invoice Details" style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Quotation No. - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Quotation No.</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.quotation_id !== currentValues.quotation_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const quotationNo = selectedQuotation?.quotation_number || '';
                          return <div>{quotationNo || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Invoice No. - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Invoice No.</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <div>{form.getFieldValue('invoice_number') || '-'}</div>
                    </td>
                  </tr>
                  {/* Date - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Date</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <div>
                        {form.getFieldValue('date') 
                          ? (dayjs.isDayjs(form.getFieldValue('date')) 
                              ? form.getFieldValue('date').format('DD/MM/YYYY')
                              : dayjs(form.getFieldValue('date')).format('DD/MM/YYYY'))
                          : '-'
                        }
                      </div>
                    </td>
                  </tr>
                  {/* Advisor - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Advisor</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="agent_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.quotation_id !== currentValues.quotation_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const advisorName = selectedQuotation?.agent?.name || '';
                          return <div>{advisorName || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Team - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Team</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="team_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.quotation_id !== currentValues.quotation_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const teamName = selectedQuotation?.team?.name || '';
                          return <div>{teamName || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Staff - 单独一行 */}
                  <tr>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Staff</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="staff_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.quotation_id !== currentValues.quotation_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const staffName = selectedQuotation?.staff?.name || '';
                          return <div>{staffName || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </Col>
        </Row>

        {/* Task Information */}
        <Card title="Task Information" style={{ marginBottom: 24 }} bodyStyle={{ padding: '16px' }}>
          <Table
            columns={[
              {
                title: 'Type',
                dataIndex: 'task_type_id',
                key: 'task_type_id',
                width: '15%',
                render: (taskTypeId: number) => {
                  const taskType = taskTypes.find(t => t.id === taskTypeId);
                  return <span>{taskType?.name || '-'}</span>;
                },
              },
              {
                title: 'Year',
                dataIndex: 'year',
                key: 'year',
                width: '10%',
                render: (year: number) => <span>{year || '-'}</span>,
              },
              {
                title: 'Task No.',
                dataIndex: 'task_template_id',
                key: 'task_no',
                width: '12%',
                render: (taskTemplateId: number) => {
                  const taskTemplate = taskTemplates.find(t => t.id === taskTemplateId);
                  return <span>{taskTemplate ? `Task #${taskTemplate.id}` : '-'}</span>;
                },
              },
              {
                title: 'Task',
                dataIndex: 'task_template_id',
                key: 'task_template_id',
                width: '18%',
                render: (taskTemplateId: number) => {
                  const taskTemplate = taskTemplates.find(t => t.id === taskTemplateId);
                  return <span>{taskTemplate?.name || '-'}</span>;
                },
              },
              {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit',
                width: '10%',
                render: (unit: number | string) => {
                  const numValue = typeof unit === 'number' ? unit : parseFloat(unit || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '-'}</span>;
                },
              },
              {
                title: 'Price/Unit',
                dataIndex: 'price_per_unit',
                key: 'price_per_unit',
                width: '12%',
                render: (pricePerUnit: number) => {
                  const numValue = typeof pricePerUnit === 'number' ? pricePerUnit : parseFloat(pricePerUnit || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Discount',
                dataIndex: 'discount',
                key: 'discount',
                width: '10%',
                render: (discount: number) => {
                  const numValue = typeof discount === 'number' ? discount : parseFloat(discount || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Amount',
                dataIndex: 'total_price',
                key: 'amount',
                width: '10%',
                render: (totalPrice: number) => {
                  const numValue = typeof totalPrice === 'number' ? totalPrice : parseFloat(totalPrice || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
            ]}
            dataSource={invoiceItems.map((item, index) => ({ ...item, key: index }))}
            pagination={false}
            size="small"
          />
        </Card>

          <Card title="Payments" style={{ marginBottom: 24 }} bodyStyle={{ padding: '16px' }}>
            <Table
              columns={paymentColumns}
              dataSource={payments.map((item, index) => ({ ...item, key: item.id || `temp-${index}` }))}
              rowKey={(record, index) => record.id || `temp-${index}`}
              pagination={false}
              size="small"
            />
            {isEditing && (
              <div style={{ marginTop: 12 }}>
                <Button
                  type="link"
                  style={{ color: '#ff4d4f', padding: 0 }}
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setPayments([...payments, {
                      id: `temp-${Date.now()}`,
                      invoice_id: parseInt(id || '0', 10),
                      type: 'official_receipt',
                      number: undefined,
                      payment_date: dayjs().format('YYYY-MM-DD'),
                      payment_amount: 0,
                      enabled: true,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    }]);
                  }}
                >
                  Add Payment
                </Button>
              </div>
            )}
          </Card>

        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
          
        }}>
        <Space>
          {isEditing && (
            <>
              <Button onClick={() => {
                if (isEdit && id) {
                  setIsEditing(false);
                  // Reload invoice data to reset form
                  loadInvoice(parseInt(id, 10));
                  loadPayments(parseInt(id, 10));
                } else {
                  // For create mode, navigate back
                  navigate('/invoices');
                }
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </>
          )}
          {!isEditing && isEdit && (
            <Button onClick={() => navigate('/invoices')}>Cancel</Button>
          )}
        </Space>
      </div>
      </Form>
    </>
  );
};
